import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, Stack, TextField, Typography } from '@mui/material';

import { getAnswersSchoolFeeding, putAnswers } from 'api/answers/answers.api';
import {
  FormName,
  GetAnswersPayload,
  PostAnswersPayload,
} from 'api/answers/dtos/answers.dto';
import { ListProducts } from 'components/cifa/list_products';
import { ContainerForm } from 'components/shared/container_form';
import { FormButton, FormButtonType } from 'components/shared/form_button';
import { HorizontalStepper } from 'components/shared/horizontal_stepper';
import { Modal } from 'components/shared/modal';
import { PageTitle } from 'components/shared/page_title';
import { SnackbarFeedback } from 'components/shared/snackbar_feedback';
import { ROUTES } from 'routes/constants';
import { NumericFormat } from 'react-number-format';
import ContainerPainel from 'components/container_painel';

export interface Product {
  name: string;
  identification: string;
  value: string;
}

export type SetProducts = React.Dispatch<React.SetStateAction<Product[]>>;


export const formatCnpj = (value?: string): string => {
  if (!value) return '';
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5',
  );
};

export const formatCpf = (cpf: string): string => {
  const cleanedCpf = cpf?.replace(/\D/g, '');
  return cleanedCpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

export const handleCnpjProductHelper = (
  name: string | undefined,
  identification: string | undefined,
  value: string,
  setCnpjProducts: SetProducts,
  setName: (s?: string) => void,
  setValue: (v: string) => void,
  setIdentification: (i?: string) => void,
  setAddModal: (m?: string) => void,
) => {
  if (name && identification && value) {
    const product: Product = { name, identification, value };

    setCnpjProducts((prevProducts) => {
      const index = prevProducts.findIndex(
        (p) => p.identification === identification,
      );
      if (index !== -1) {
        const newList = [...prevProducts];
        newList[index] = product;
        return newList;
      }
      return [...prevProducts, product];
    });

    setName(undefined);
    setValue('');
    setIdentification(undefined);
    setAddModal(undefined);
  }
};

export const handleCpfProductHelper = (
  name: string | undefined,
  identification: string | undefined,
  value: string,
  setCpfProducts: SetProducts,
  setValue: (v: string) => void,
  setAddModal: (m?: string) => void,
  setName: (s?: string) => void,
  setIdentification: (i?: string) => void,
) => {
  if (name && identification && value) {
    const product: Product = { name, identification, value };

    setCpfProducts((prevProducts) => {
      const idx = prevProducts.findIndex(
        (item) => item.identification === identification,
      );
      if (idx !== -1) {
        const updated = [...prevProducts];
        updated[idx] = product;
        return updated;
      }
      return [...prevProducts, product];
    });

    setValue('');
    setAddModal(undefined);
    setName(undefined);
    setIdentification(undefined);
  }
};

export const handleDeleteCnpjProductHelper = (
  id: string,
  setCnpjProducts: SetProducts,
  setName: (s?: string) => void,
  setValue: (v: string) => void,
  setIdentification: (i?: string) => void,
) => {
  setCnpjProducts((prev) => prev.filter((item) => item.identification !== id));
  setName(undefined);
  setValue('');
  setIdentification(undefined);
};

export const handleDeleteCpfProductHelper = (
  id: string,
  setCpfProducts: SetProducts,
  setValue: (v: string) => void,
  setIdentification: (i?: string) => void,
  setName: (s?: string) => void,
) => {
  setCpfProducts((prevList) => prevList.filter((el) => el.identification !== id));
  setValue('');
  setIdentification(undefined);
  setName(undefined);
};

const EditMunicipalSchoolFeeding2025: React.FC = () => {
  const navigate = useNavigate();

  const [provided, setProvided] = useState<string>('');
  const [applied, setApplied] = useState<string>('');

  const [addModal, setAddModal] = useState<string | undefined>(undefined);
  const [name, setName] = useState<string>();
  const [identification, setidentification] = useState<string>();
  const [value, setValue] = useState<string>('');
  const [id, setId] = useState<string>();

  const [cnpjProducts, setCnpjProducts] = useState<Product[]>([]);
  const [cpfProducts, setCpfProducts] = useState<Product[]>([]);

  const [loading, setLoading] = useState<boolean>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [sucessModal] = useState<boolean>(false);

  const handleCnpjProduct = () => {
    handleCnpjProductHelper(
      name,
      identification,
      value,
      setCnpjProducts,
      setName,
      setValue,
      setidentification,
      setAddModal,
    );
  };

  const handleCpfProduct = () => {
    handleCpfProductHelper(
      name,
      identification,
      value,
      setCpfProducts,
      setValue,
      setAddModal,
      setName,
      setidentification,
    );
  };

  const handleDeleteCnpjProduct = (id: string) => {
    handleDeleteCnpjProductHelper(id, setCnpjProducts, setName, setValue, setidentification);
  };

  const handleDeleteCpfProduct = (id: string) => {
    handleDeleteCpfProductHelper(id, setCpfProducts, setValue, setidentification, setName);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const userAccessUuid = localStorage.getItem('userAccessUuid') ?? '';
    const institutionUuid = localStorage.getItem('institutionUuid') ?? '';

    const payload: PostAnswersPayload = {
      userAccessUuid,
      institutionUuid,
      formName: FormName.ALIMENTACAO_ESCOLAR_MUNICIPAL,
      answer: {
        valueProvidedByPnae: parseFloat(provided.replace(/[^\d.-]/g, '')) || 0,
        valueAppliedByPnaeResource:
          parseFloat(applied.replace(/[^\d.-]/g, '')) || 0,
        productAquiredFromCnpjByPnaeResourceList: cnpjProducts,
        productAquiredFromCpfByPnaeResourceList: cpfProducts,
      },
      referencedYear: 2025,
    };

    if (id) {
      try {
        await putAnswers(payload, id);
        navigate(ROUTES.EDIT_CONFIRM_SCHOOL_FEEDING_2025);
      } catch (error) {
        setErrorMessage(
          'Erro ao atualizar os dados da Alimentação Escolar (PNAE Municipal).',
        );
      } finally {
        setLoading(false);
      }
    }
  };


  useEffect(() => {
    const fetchData = async () => {
      const institutitonUuid = localStorage.getItem('institutionUuid') ?? '';
      const userAccessUuid = localStorage.getItem('userAccessUuid') ?? '';

      const payload: GetAnswersPayload = {
        institutitonUuid,
        userAccessUuid,
        referencedYear: 2025,
      };

      try {
        const response = await getAnswersSchoolFeeding(payload);
        const form = response?.data.find(
          (form: any) => form.formName === 'ALIMENTAÇÃO ESCOLAR FEDERAL',
        );

        if (form) {
          setCpfProducts(form?.answer?.productAquiredFromCpfByPnaeResourceList);
          setCnpjProducts(form?.answer?.productAquiredFromCnpjByPnaeResourceList);
          setApplied(form?.answer?.valueAppliedByPnaeResource);
          setProvided(form?.answer?.valueProvidedByPnae);
          setId(form?.uuid);
        }
      } catch (error) {
        setErrorMessage(
          'Erro ao recuperar os dados da Aba Alimentação Escolar (Federal).',
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);


  return (
    <ContainerPainel
      pagina='CIFA - Editar Município - 2025'
      showBackButton={true}
      backButtonPath={ROUTES.CIFA2025}
    >
      <HorizontalStepper activeStep={3} />

      <PageTitle
        title='Alimentação Escolar (PNAE Municipal)'
        subtitle='Informe corretamente os dados sobre a aquisição de Alimentação Escolar através do PNAE Municipal no Município.'
      />

      <Stack marginX='4rem'>
        <ContainerForm title='Valores para Aquisição de Alimentação Escolar - Em 2024'>
          <Stack direction='row' justifyContent='space-between' gap='1rem'>
            <NumericFormat
              id='aplicado'
              label='Valores totais aplicados pelo município na Alimentação Escolar oriundos de todo e qualquer fornecedor no ano de 2024 '
              value={applied}
              onValueChange={(values) => setApplied(values.value)}
              thousandSeparator='.'
              decimalSeparator=','
              prefix='R$ '
              customInput={TextField}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <NumericFormat
              id='disponibilizado'
              label='Valor disponibilizado pelo PNAE Federal em R$:'
              value={provided}
              onValueChange={(values) => setProvided(values.value)}
              thousandSeparator='.'
              decimalSeparator=','
              prefix='R$ '
              customInput={TextField}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </ContainerForm>

        <ContainerForm title='Produtos adquiridos via CNPJ (com recurso do PNAE Municipal) - Valores Aplicados na Aquisição de Produtos (Opcional)'>
          <FormButton
            type={FormButtonType.PRIMARY}
            title='+ Adicionar Aquisição'
            onClick={() => setAddModal('CNPJ')}
          />

          {cnpjProducts.length > 0 && (
            <ListProducts
              products={cnpjProducts}
              documentType={'CNPJ'}
              edit={(value) => {
                setName(value.name);
                setidentification(value.identification);
                setValue(value.value);
                setAddModal('CNPJ');
              }}
              remove={(value) => handleDeleteCnpjProduct(value.identification)}
            />
          )}

          {cnpjProducts.length === 0 && (
            <Alert
              severity='info'
              sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
            >
              <Typography
                variant='body2'
                fontSize='14px'
                textAlign='left'
                lineHeight={1.5}
              >
                Você ainda não realizou a inclusão de nenhuma Aquisição de
                Produto via CNPJ. Clique no botão abaixo ”Adicionar Aquisição”
                para incluir uma Aquisição de Produto.
              </Typography>
            </Alert>
          )}
        </ContainerForm>

        <ContainerForm title='Produtos adquiridos via CPF (com recurso do PNAE Municipal) - Valores Aplicados na Aquisição de Produtos (Opcional)'>
          <FormButton
            type={FormButtonType.PRIMARY}
            title='+ Adicionar Aquisição'
            onClick={() => setAddModal('CPF')}
          />

          {cpfProducts.length > 0 && (
            <ListProducts
              products={cpfProducts}
              documentType={'CPF'}
              edit={(value) => {
                setName(value.name);
                setidentification(value.identification);
                setValue(value.value);
                setAddModal('CPF');
              }}
              remove={(value) => handleDeleteCpfProduct(value.identification)}
            />
          )}

          {cpfProducts.length === 0 && (
            <Alert
              severity='info'
              sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
            >
              <Typography
                variant='body2'
                fontSize='14px'
                textAlign='left'
                lineHeight={1.5}
              >
                Você ainda não realizou a inclusão de nenhuma Aquisição de
                Produto via CPF. Clique no botão abaixo ”Adicionar Aquisição”
                para incluir uma Aquisição de Produto.
              </Typography>
            </Alert>
          )}
        </ContainerForm>

        <Stack
          direction={{ md: 'row-reverse' }}
          marginY='2rem'
          sx={{ gap: '1rem' }}
        >
          <FormButton
            title='Avançar'
            type={FormButtonType.PRIMARY}
            onClick={handleSubmit}
            isDisabled={loading}
          />
          <FormButton
            title='Voltar'
            type={FormButtonType.SECONDARY}
            onClick={() => navigate(ROUTES.EDIT_STATE_SCHOOL_FEEDING_2025)}
          />
        </Stack>

        {
          addModal &&
          <Modal
            open={!!addModal}
            onClose={() => setAddModal(undefined)}
            title={`Adicionar Aquisição via ${addModal} - Produtos adquiridos com recurso do PNAE Municipal`}
            directionButtons='row'
            buttons={[
              <FormButton
                key={0}
                type={FormButtonType.PRIMARY}
                title='Confirmar'
                onClick={() =>
                  addModal === 'CNPJ' ? handleCnpjProduct() : handleCpfProduct()
                }
              />,
              <FormButton
                key={1}
                type={FormButtonType.SECONDARY}
                title='Voltar'
                onClick={() => {
                  setAddModal(undefined);
                  setName(undefined);
                  setidentification(undefined);
                  setValue('');
                }}
              />,
            ]}
          >
            <>
              <TextField
                id='nome'
                label='Nome/Razão social'
                variant='outlined'
                value={name}
                onChange={(e) => setName(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Stack
                direction='row'
                justifyContent='space-between'
                gap='1rem'
                mt='1rem'
              >
                <NumericFormat
                  id='valor'
                  label='Valor'
                  variant='outlined'
                  value={value}
                  onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
                  thousandSeparator='.'
                  decimalSeparator=','
                  prefix='R$ '
                  customInput={TextField}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  id='documento'
                  label={addModal}
                  variant='outlined'
                  value={
                    addModal === 'CPF'
                      ? formatCpf(identification as string)
                      : formatCnpj(identification as string)
                  }
                  onChange={(e) =>
                    setidentification(e.target.value.replace(/\D/g, ''))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    maxLength: addModal === 'CPF' ? 14 : 18,
                  }}
                  sx={{ flex: 1 }}
                />
              </Stack>
            </>
          </Modal>

        }

        <Modal
          open={sucessModal}
          onClose={() => navigate(ROUTES.CIFA2025)}
          title='Sucesso ao editar munícipio!'
          subTitle='O município foi editado com o sucesso no CIFA 2025'
          buttons={[
            <FormButton
              key={1}
              type={FormButtonType.PRIMARY}
              title='Voltar para a tela inicial'
              onClick={() => navigate(ROUTES.CIFA2025)}
            />,
          ]}
        />

        {errorMessage && (
          <SnackbarFeedback isOpen type='error' message={errorMessage} />
        )}
      </Stack>
    </ContainerPainel>
  );
};

export default EditMunicipalSchoolFeeding2025;
